/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {Karen} from "../../../Karen";
import {KarenGameConfig} from "../../../KarenGameConfig";
import {BetWinMode} from "../../../../core/config/BetWinMode";
import {IBetWayWinData, IBetWinData} from "../../../server/data/IKarenData";
import {KarenSymbol} from "../../../symbol/KarenSymbol";
import {KarenParsedGameData} from "../../../server/data/KarenParsedGameData";
import {KarenSoundManager} from "../../../sound/KarenSoundManager";
import {KarenSoundConfig} from "../../../sound/KarenSoundConfig";
import {GameHelper} from "../../../../core/utils/GameHelper";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {KarenGameMode} from "../../../server/KarenGameMode";

export class InitialWinController extends WinPresentationController {

    private _mainTimeline: TimelineLite | null;
    private _isWinAboveMediumInFS: boolean = false;
    private _startTime: number = 0;

    constructor() {
        super("InitialWinController", true);
    }

    protected hasWin(): boolean {
        if (Karen.model.parsedGameData.isStickyFSParticipatingWin) {
            Karen.xwaysMultiplierView.hideNoOfWays()
        }
        return Karen.model.parsedGameData.betWins?.length > 0;
    }

    protected async createWinAnimation(): Promise<any> {
        await new Promise<void>(resolve => {
            this.getAllAnimations(resolve);
        });
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        this._isWinAboveMediumInFS = false;
        let isBigWin: boolean = false;
        const winRatio: number = Karen.model.parsedGameData.currentWinRatioConfig?.ratio!;
        const winTypeName: string = Karen.model.parsedGameData.currentWinRatioConfig?.name!;
        const tl: TimelineLite = gsap.timeline();
        let winDelay: number = 0;
        if (!Karen.model.parsedGameData.winBelowStack) {
            Karen.model.parsedGameData.betWins.forEach(w => {
                if (KarenGameConfig.BET_WIN_MODE === BetWinMode.BET_WAY) {
                    tl.add(this.getSingleBetWayWinAnimation(<IBetWayWinData>w, winRatio, winTypeName), winDelay);
                }
            });
            if (winRatio >= KarenGameConfig.WIN_RATIOS[4].ratio) {
                isBigWin = true;
            }
            if (isBigWin) {
                tl.add(() => {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.BIGWIN_INTRO);
                    KarenSoundManager.playDoNotLoopSound(KarenSoundConfig.BIGWIN_INTRO_MUSIC);
                    if (Karen.model.parsedGameData.mode === KarenGameMode.FREESPIN) {
                        KarenSoundManager.fadeFSGameSound(200, 0);
                        KarenSoundManager.fadeFSLoopSound(200);
                    } else {
                        KarenSoundManager.fadeMusic(
                            0, 200, KarenSoundConfig.MAIN_GAME_MUSIC
                        );
                    }

                }, 0);
            }
        }
        if (!isBigWin && winRatio > KarenGameConfig.WIN_RATIOS[2].ratio && Karen.model.parsedGameData.mode === KarenGameMode.FREESPIN) {
            tl.add(() => {
                KarenSoundManager.playFSLoopSound();
            });
        }
        return tl;
    }

    protected getSingleBetWayWinAnimation(winData: IBetWayWinData, winRatio: number, winTypeName: string): TimelineLite {
        const tl: TimelineLite = gsap.timeline();
        let symbolDelay: number = 0;
        const reelDelay: number = this.getReelDelay(winRatio);
        let winType: string = winTypeName.split(' ')[0].toLowerCase();
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let hasReelWin: boolean = false;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winTypeName), symbolDelay);
                }
            });
            if (winType === "big") {
                tl.add(() => {KarenSoundManager.playSoundEffect(KarenSoundConfig.WIN_FRAME[reelId])}, symbolDelay);
            }
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, winTypeName: string): TimelineLite {
        const tl: TimelineLite = gsap.timeline();
        const sym: KarenSymbol = Karen.reels[reelId].getSymbol(symId, false, false);
        tl.add(() => Karen.reels[reelId].reelView.addWinSymbol(sym));
        let winType: string = winTypeName.split(' ')[0].toLowerCase();
        if (winType === "tiny") {
            tl.add(sym.playWin("tiny_toggle", true, 0));
        } else if (winType === "big") {
            tl.add(sym.playWin("large", false, 0));
        } else {
            tl.add(sym.playWin(winType, true, 0));
        }
        return tl;
    }

    protected getWinCountUpAnimation(): TimelineLite {
        const data: KarenParsedGameData = Karen.model.parsedGameData;
        const tl: TimelineLite = gsap.timeline();
        let winRatioIndex: number = data.currentWinRatioConfig!.winRatioIndex ? data.currentWinRatioConfig!.winRatioIndex : 0;
        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        // #215 - For wins less than base bet dont to the pump up of the win value
        if ((Karen.model.parsedGameData.singleWin <= Karen.model.parsedGameData.playedBetValue) && !Karen.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            tl.add(() => {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.WIN_SOUNDS[winRatioIndex]);
            });
            const duration: number = data.currentWinRatioConfig!.duration;
            tl.add(Karen.initialCountUp.showCountUp(data.singleWin, Karen.model.parsedGameData.totalNumberOfCombinations));
        } else if (!data.winBelowStack) {
            tl.add(() => {
                if (winRatioIndex > 1) {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.COUNTUP, 1);
                }
                KarenSoundManager.playSoundEffect(KarenSoundConfig.WIN_SOUNDS[winRatioIndex]);
            });
            const duration: number = data.currentWinRatioConfig!.duration;
            tl.add(Karen.initialCountUp.getCountUpAnimation(0, data.singleWin, duration, data.currentMultiplier, Karen.model.parsedGameData.totalNumberOfCombinations));
            let tlDur: number = duration - 0.8;
            // if (tlDur < 0) {
            //     tlDur = 0;
            // }
            tl.add(Karen.initialCountUp.showCountEffects(winRatioIndex <= 1 ? "small" : "medium"), "-=" + tlDur);
            // Playing sound once only in case of medium/large win.
            if (winRatioIndex > 1) {
                tl.add(() => {
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.COUNTUP);
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.COUNTUP_ENDS[winRatioIndex]);
                }, duration);
            }
        }
        tl.add(() => SlotGame.keypadPlugin.setWin(data.totalWin, Karen.model.parsedGameData.showTotalWin, GameHelper.isWinBelowStake()), 0.01);
        return tl;
    }

    protected skip(): void {
        //this._mainTimeline?.progress(1);
        //#196 donot skip initial win
    }

    private getAllAnimations(resolve: () => void): void {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                resolve();
            }
        });
        for(let i:number=0;i<Karen.model.parsedGameData.betWins.length;i++){
            const betWinData:IBetWayWinData=<IBetWayWinData>Karen.model.parsedGameData.betWins[i],
                individualSymTL:gsap.core.Timeline=gsap.timeline();
            let lastWinSym;
            for(let reelIdx=0;reelIdx<betWinData.reelWinPositions.length;reelIdx++){
                for(let symIdx:number=0;symIdx<betWinData.reelWinPositions[reelIdx].length;symIdx++){
                    if(betWinData.reelWinPositions[reelIdx][symIdx]){
                        const sym=Karen.reels[reelIdx].getSymbol(symIdx);
                        individualSymTL.add(this.playAnimation(sym,"Tiny win"),0);
                        lastWinSym=[reelIdx,symIdx];
                    }
                }
            }
            if(!GameHelper.isWinBelowStake()){
                tl.add([individualSymTL,Karen.reels[lastWinSym![0]].getSymbol(lastWinSym![1]).showWaysAndAmount(betWinData.multipliedAmount,betWinData.numberOfCombinations,0.5)]);
            }
        }
        tl.to(this,{duration:0.4});
        tl.add([
            this.getWinAnimation(),
            this.getWinCountUpAnimation()
        ]);
        this._mainTimeline = tl;
    }
    private playAnimation(symbol: KarenSymbol, winTypeName: string): TimelineLite {
        //details copied from oktoberfest original
        let timeline: TimelineLite = new TimelineLite();
        const postFix: string = "_toggle";
        let winType: string = winTypeName.split(' ')[0].toLowerCase();
        if (winType === "tiny" || winType === "small") {
            timeline.add(symbol.playWin("tiny" + postFix, true, 0));
        } else {
            timeline.add(symbol.playWin("win" + postFix, true, 0));
        }
        return timeline;
    }

    private getReelDelay(winRatio: number): number {
        // tiny/small
        if (winRatio < KarenGameConfig.WIN_RATIOS[2].ratio) {
            return 0;
        }

        //mid
        if (winRatio < KarenGameConfig.WIN_RATIOS[3].ratio) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }
}
